import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Wallet, TrendingUp, Receipt, RefreshCw,
  Landmark, CalendarClock, LineChart, Eye, EyeOff, LogOut, Menu, X, Radar, PieChart, Users, Tags, ShieldCheck, Umbrella, Database, Store, BarChart3, Sparkles, Bitcoin, Bell, Shield, Rocket, Skull, Boxes,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { usePrivacy } from "../context/PrivacyContext";
import { useFeatureFlags } from "../context/FeatureFlagsContext";
import api, { apiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

const NAV_SECTIONS = [
  {
    label: null,
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard },
      { to: "/patrimonio", label: "Patrimonio", icon: PieChart },
      { to: "/activos", label: "Activos", icon: Wallet },
      { to: "/inversiones", label: "Inversiones", icon: Sparkles },
      { to: "/cripto", label: "Activos Financieros", icon: Bitcoin, flag: "crypto_module" },
      { to: "/ingresos", label: "Ingresos", icon: TrendingUp },
      { to: "/gastos", label: "Gastos", icon: Receipt },
      { to: "/compromisos", label: "Compromisos", icon: RefreshCw },
      { to: "/obligaciones", label: "Obligaciones", icon: Landmark },
      { to: "/tesoreria", label: "Tesorería", icon: Landmark },
      { to: "/economia-miembro", label: "Economía individual", icon: Landmark },
      { to: "/seguros", label: "Seguros", icon: Umbrella },
    ],
  },
  {
    label: "Planificación",
    items: [
      { to: "/pagos", label: "Pagos pendientes", icon: CalendarClock },
      { to: "/proyecciones", label: "Proyecciones", icon: LineChart },
      { to: "/libertad-financiera", label: "Libertad financiera", icon: Rocket },
      { to: "/gastos-zombie", label: "Gastos Zombie", icon: Skull },
      { to: "/analitica", label: "Analítica", icon: BarChart3 },
    ],
  },
  {
    label: "Comunicaciones",
    items: [
      { to: "/notificaciones", label: "Notificaciones", icon: Bell },
      { to: "/renovaciones", label: "Renovaciones", icon: CalendarClock },
      { to: "/copiloto", label: "Copiloto", icon: Sparkles },
    ],
  },
  {
    label: "Configuración",
    items: [
      { to: "/familia", label: "Familia", icon: Users },
      { to: "/listado-valores", label: "Listado de Valores", icon: Store },
      { to: "/datos", label: "Exportar / Importar", icon: Database },
    ],
  },
  {
    label: "Administración",
    items: [
      { to: "/matriz", label: "MATRIZ", icon: Shield, matrizOnly: true },
      { to: "/scme", label: "Ecosistema de Plugins", icon: Boxes, matrizOnly: true },
    ],
  },
];

// vista aplanada retrocompatible (se sigue usando en algunos sitios)
const NAV = NAV_SECTIONS.flatMap((s) => s.items);

const MOBILE_NAV = [
  { to: "/", label: "Inicio", icon: LayoutDashboard },
  { to: "/patrimonio", label: "Patrimonio", icon: PieChart },
  { to: "/gastos", label: "Gastos", icon: Receipt },
  { to: "/obligaciones", label: "Obligac.", icon: Landmark },
];

function PrivacyToggle() {
  const { hidden, setHidden } = usePrivacy();
  const { user, refreshUser } = useAuth();
  const [dialog, setDialog] = useState(false);
  const [pin, setPin] = useState("");
  const [mode, setMode] = useState("verify");

  const onToggle = () => {
    if (!hidden) {
      if (!user?.has_pin) { setMode("set"); setDialog(true); return; }
      setHidden(true);
    } else {
      if (user?.has_pin) { setMode("verify"); setDialog(true); return; }
      setHidden(false);
    }
  };

  const submit = async () => {
    try {
      if (mode === "set") {
        await api.post("/auth/pin", { pin });
        await refreshUser();
        setHidden(true);
        toast.success("PIN creado. Importes ocultos.");
      } else {
        const { data } = await api.post("/auth/pin/verify", { pin });
        if (!data.valid) { toast.error("PIN incorrecto"); return; }
        setHidden(false);
      }
      setDialog(false);
      setPin("");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <>
      <button
        onClick={onToggle}
        data-testid="privacy-toggle-button"
        className="h-9 w-9 flex items-center justify-center rounded-lg border border-border hover:bg-secondary transition-colors"
        title={hidden ? "Mostrar importes" : "Ocultar importes"}
      >
        {hidden ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {mode === "set" ? "Crea un PIN de 4 dígitos" : "Introduce tu PIN"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">
            {mode === "set" ? "Se usará para volver a mostrar los importes." : "Necesario para mostrar los importes."}
          </p>
          <Input
            data-testid="pin-input"
            type="password"
            inputMode="numeric"
            maxLength={4}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
            placeholder="••••"
            className="text-center text-2xl tracking-[0.5em]"
            onKeyDown={(e) => e.key === "Enter" && pin.length === 4 && submit()}
          />
          <Button data-testid="pin-submit-button" onClick={submit} disabled={pin.length !== 4} className="w-full">
            {mode === "set" ? "Crear PIN y ocultar" : "Mostrar importes"}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { flags, isMatriz, dismiss, refresh } = useFeatureFlags();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => { if (user) refresh(); }, [user?.id]);  // eslint-disable-line

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
      isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
    }`;

  // Filtrar NAV según feature flags + matriz-only
  const filterItem = (it) => {
    if (it.matrizOnly && !isMatriz) return false;
    if (it.flag && !flags[it.flag]?.enabled) return false;
    return true;
  };
  const visibleNav = NAV.filter(filterItem);
  const visibleSections = NAV_SECTIONS
    .map((s) => ({ ...s, items: s.items.filter(filterItem) }))
    .filter((s) => s.items.length > 0);
  // Banners de activaciones recientes no descartadas
  const activationBanners = Object.entries(flags).filter(
    ([, v]) => v.enabled && v.announced && !v.notification_dismissed
  );

  const renderSidebarSections = () => visibleSections.map((section, i) => (
    <div key={section.label || `s-${i}`} className={i > 0 ? "pt-3 mt-3 border-t border-border/40" : ""}>
      {section.label && (
        <div className="px-3 pb-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70 font-bold">{section.label}</div>
      )}
      {section.items.map(({ to, label, icon: Icon }) => (
        <NavLink key={to} to={to} end={to === "/"} className={linkCls} data-testid={`nav-${to === "/" ? "dashboard" : to.slice(1)}`}>
          <Icon size={16} />
          {label}
        </NavLink>
      ))}
    </div>
  ));

  return (
    <div className="min-h-screen bg-background text-left">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-60 flex-col border-r border-border bg-background z-30">
        <div className="flex items-center gap-2 px-5 h-16 border-b border-border">
          <Radar size={20} strokeWidth={2.5} />
          <span className="font-heading font-extrabold tracking-tight text-base leading-none">RADAR<br /><span className="text-[10px] tracking-[0.25em] text-muted-foreground font-bold">FINANCIERO</span></span>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
          {renderSidebarSections()}
        </nav>
        <div className="p-3 border-t border-border">
          <NavLink to="/perfil" data-testid="nav-perfil" className={linkCls}>
            <ShieldCheck size={16} /> Perfil y seguridad
          </NavLink>
          <div className="px-3 py-2 text-xs text-muted-foreground truncate">{user?.email}</div>
          <button onClick={doLogout} data-testid="logout-button" className="flex w-full items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
            <LogOut size={16} /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Top bar */}
      <header className="fixed top-0 left-0 right-0 lg:left-60 h-16 border-b border-border bg-background/95 backdrop-blur z-20 flex items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-2 lg:hidden">
          <Radar size={18} strokeWidth={2.5} />
          <span className="font-heading font-extrabold tracking-tight">RADAR FINANCIERO</span>
        </div>
        <div className="hidden lg:block text-sm text-muted-foreground">
          {new Date().toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </div>
        <div className="flex items-center gap-2">
          <PrivacyToggle />
          <button onClick={() => setMenuOpen(true)} data-testid="mobile-menu-button" className="lg:hidden h-9 w-9 flex items-center justify-center rounded-lg border border-border">
            <Menu size={16} />
          </button>
        </div>
      </header>

      {/* Mobile full menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 bg-background lg:hidden" data-testid="mobile-menu">
          <div className="flex items-center justify-between h-16 px-4 border-b border-border">
            <span className="font-heading font-extrabold">MENÚ</span>
            <button onClick={() => setMenuOpen(false)} data-testid="mobile-menu-close" className="h-9 w-9 flex items-center justify-center rounded-lg border border-border"><X size={16} /></button>
          </div>
          <nav className="p-4 space-y-1 overflow-y-auto max-h-full">
            {visibleSections.map((section, i) => (
              <div key={section.label || `ms-${i}`} className={i > 0 ? "pt-3 mt-3 border-t border-border/40" : ""}>
                {section.label && <div className="px-3 pb-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70 font-bold">{section.label}</div>}
                {section.items.map(({ to, label, icon: Icon }) => (
                  <NavLink key={to} to={to} end={to === "/"} onClick={() => setMenuOpen(false)} className={linkCls}>
                    <Icon size={18} /> {label}
                  </NavLink>
                ))}
              </div>
            ))}
            <div className="pt-3 mt-3 border-t border-border/40">
              <NavLink to="/perfil" onClick={() => setMenuOpen(false)} className={linkCls}>
                <ShieldCheck size={18} /> Perfil y seguridad
              </NavLink>
              <button onClick={doLogout} className="flex w-full items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary">
                <LogOut size={18} /> Cerrar sesión
              </button>
            </div>
          </nav>
        </div>
      )}

      {/* Content */}
      <main className="pt-16 lg:pl-60 pb-20 lg:pb-8">
        <div className="max-w-6xl mx-auto p-4 sm:p-6">
          {activationBanners.length > 0 && (
            <div className="mb-4 space-y-2" data-testid="feature-banners">
              {activationBanners.map(([key, meta]) => (
                <div key={key} className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3 flex items-start gap-3" data-testid={`banner-${key}`}>
                  <Sparkles size={16} className="mt-0.5 text-emerald-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">🎉 Nueva funcionalidad disponible: {meta.label}</div>
                    <div className="text-xs text-emerald-800/90 dark:text-emerald-200/80 mt-0.5">{meta.description}</div>
                  </div>
                  <button
                    onClick={() => dismiss(key)}
                    className="text-xs opacity-70 hover:opacity-100 shrink-0"
                    data-testid={`banner-close-${key}`}
                    aria-label="Cerrar"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
          {children}
        </div>
      </main>

      {/* Bottom nav mobile */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 h-16 bg-background border-t border-border z-30 grid grid-cols-5" data-testid="bottom-nav">
        {[...MOBILE_NAV, { to: "/pagos", label: "Pagos", icon: CalendarClock }].map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold transition-colors ${isActive ? "text-foreground" : "text-muted-foreground"}`
            }
          >
            <Icon size={18} />
            {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
